import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { partTypesApi } from './service';
import type { PartTypeCreateValues, PartTypeUpdateValues } from './schema';
import type { PartType } from './types';

/**
 * Query key factory — one place to change the cache layout.
 *
 *   partTypesKeys.all         every cached part-types query
 *   partTypesKeys.lists()     all list queries (future: filters)
 *   partTypesKeys.detail(id)  a single part type
 */
export const partTypesKeys = {
  all: ['part-types'] as const,
  lists: () => [...partTypesKeys.all, 'list'] as const,
  detail: (id: string) => [...partTypesKeys.all, 'detail', id] as const,
};

export function usePartTypes() {
  return useQuery({
    queryKey: partTypesKeys.lists(),
    queryFn: partTypesApi.list,
  });
}

export function usePartType(id: string | undefined) {
  return useQuery({
    queryKey: id ? partTypesKeys.detail(id) : partTypesKeys.all,
    queryFn: () => partTypesApi.getById(id as string),
    enabled: Boolean(id),
  });
}

/**
 * Mutations — same plain-invalidation pattern as EquipmentTypes
 * (no optimistic updates yet; the offline path lands in F12).
 */
export function useCreatePartType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PartTypeCreateValues) => partTypesApi.create(input),
    onSuccess: (created: PartType) => {
      qc.invalidateQueries({ queryKey: partTypesKeys.lists() });
      qc.setQueryData(partTypesKeys.detail(created.id), created);
    },
  });
}

export function useUpdatePartType(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: PartTypeUpdateValues) =>
      partTypesApi.update(id, patch),
    onSuccess: (updated: PartType) => {
      qc.invalidateQueries({ queryKey: partTypesKeys.lists() });
      qc.setQueryData(partTypesKeys.detail(id), updated);
    },
  });
}

export function useDeletePartType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => partTypesApi.remove(id),
    onSuccess: (_void, id) => {
      qc.invalidateQueries({ queryKey: partTypesKeys.lists() });
      qc.removeQueries({ queryKey: partTypesKeys.detail(id) });
    },
  });
}
